import { formatCourseDurationHours } from '@/lib/course-duration';
import type { CourseFormValues } from './CourseFormModern';

interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}

export function flattenCategories(nodes: CategoryNode[], depth = 0): { value: string; label: string }[] {
  return nodes.flatMap((node) => [
    { value: node.id, label: `${'— '.repeat(depth)}${node.name}` },
    ...(node.children ? flattenCategories(node.children, depth + 1) : []),
  ]);
}

export function buildCourseFormData(values: CourseFormValues, coverFile: File | null, bannerFile: File | null) {
  const formData = new FormData();
  formData.append('name', values.name);
  formData.append('code', values.code);
  if (values.slug.trim()) formData.append('slug', values.slug.trim());
  formData.append('status', values.status);
  if (values.shortDescription.trim()) formData.append('short_description', values.shortDescription.trim());
  if (values.fullDescription.trim()) formData.append('full_description', values.fullDescription.trim());
  formData.append('category_id', values.categoryId);
  formData.append('visibility', values.visibility);
  formData.append('primary_instructor_id', values.primaryInstructorId);
  values.additionalInstructors.forEach((instructorId) => formData.append('additional_instructors[]', instructorId));
  values.instructorRoleIds.forEach((roleId) => formData.append('instructor_role_ids[]', roleId));
  formData.append('is_free', values.isFree ? '1' : '0');
  if (!values.isFree && values.price.trim()) formData.append('price', values.price.trim());
  formData.append('requires_enrollment', values.requiresEnrollment ? '1' : '0');
  formData.append('allow_comments', values.allowComments ? '1' : '0');
  formData.append('show_progress', values.showProgress ? '1' : '0');
  if (values.minCompliancePercent.trim()) {
    formData.append('min_compliance_percent', values.minCompliancePercent.trim());
  }
  if (values.academicHoursTime.trim()) formData.append('academic_hours_time', values.academicHoursTime.trim());
  if (values.startDate) formData.append('start_date', values.startDate);
  if (values.endDate) formData.append('end_date', values.endDate);
  if (values.maxEnrollment.trim()) formData.append('max_enrollment', values.maxEnrollment.trim());
  if (values.metaTitle.trim()) formData.append('meta_title', values.metaTitle.trim());
  if (values.metaDescription.trim()) formData.append('meta_description', values.metaDescription.trim());
  if (values.contractTemplateId.trim()) {
    formData.append('contract_template_id', values.contractTemplateId.trim());
  } else {
    formData.append('contract_template_id', '');
  }
  formData.append('requires_prerequisite_course', values.requiresPrerequisiteCourse ? '1' : '0');
  formData.append('prerequisite_course_id', values.requiresPrerequisiteCourse ? values.prerequisiteCourseId.trim() : '');
  formData.append(
    'prerequisite_assessment_id',
    values.requiresPrerequisiteCourse ? values.prerequisiteAssessmentId.trim() : '',
  );
  if (coverFile) formData.append('cover_image', coverFile);
  if (bannerFile) formData.append('banner_image', bannerFile);
  return formData;
}

export function courseToFormValues(course: Record<string, unknown>): CourseFormValues {
  const manual = course.manualInstructorIds as string[] | undefined;
  const additional =
    manual ??
    (course.instructors as { userId: string }[] | undefined)?.map((row) => row.userId) ?? [];
  const instructorRoleIds = (course.instructorRoleIds as string[] | undefined) ?? [];

  const startDate = course.startDate as string | null | undefined;
  const endDate = course.endDate as string | null | undefined;

  return {
    name: String(course.name ?? ''),
    code: String(course.code ?? ''),
    slug: String(course.slug ?? ''),
    status: String(course.status ?? 'draft'),
    shortDescription: String(course.shortDescription ?? ''),
    fullDescription: String(course.fullDescription ?? ''),
    categoryId: String((course.categoryId as string | undefined) ?? (course.category as { id?: string } | undefined)?.id ?? ''),
    visibility: String(course.visibility ?? 'public'),
    primaryInstructorId: String((course.primaryInstructorId as string | undefined) ?? (course.primaryInstructor as { id?: string } | undefined)?.id ?? ''),
    additionalInstructors: additional,
    supervisorUserIds: (course.supervisorUserIds as string[] | undefined) ?? [],
    instructorRoleIds,
    isFree: Boolean(course.isFree ?? true),
    price: course.price != null ? String(course.price) : '',
    requiresEnrollment: Boolean(course.requiresEnrollment ?? true),
    allowComments: Boolean(course.allowComments ?? true),
    showProgress: Boolean(course.showProgress ?? true),
    minCompliancePercent:
      course.minCompliancePercent != null && course.minCompliancePercent !== ''
        ? String(course.minCompliancePercent)
        : '',
    academicHoursTime: formatCourseDurationHours(course.academicHours as number | string | null),
    startDate: startDate ? String(startDate).slice(0, 10) : '',
    endDate: endDate ? String(endDate).slice(0, 10) : '',
    maxEnrollment: course.maxEnrollment != null ? String(course.maxEnrollment) : '',
    metaTitle: String(course.metaTitle ?? ''),
    metaDescription: String(course.metaDescription ?? ''),
    contractTemplateId: String(course.contractTemplateId ?? ''),
    requiresPrerequisiteCourse: Boolean(course.requiresPrerequisiteCourse),
    prerequisiteCourseId: String(course.prerequisiteCourseId ?? ''),
    prerequisiteAssessmentId: String(course.prerequisiteAssessmentId ?? ''),
  };
}
